// @ts-check
import React, { useCallback, useMemo } from 'react';
import {
  Spinner,
  ValidatedOptions,
  Select,
  SelectList,
  SelectOption,
  KeyTypes
} from '@patternfly/react-core';

import { useTranslation } from 'localization/useTranslation';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useMenuToggle } from './useMenuToggle';
import useBoolean from 'utils/hooks/useBoolean';
import { format } from 'utils/string';
/**
 * @template ValueType
 * @typedef {Object} OptionType
 * @prop {string} text
 * @prop {ValueType} value
 * @prop {string} [description] - Optional description
 */

/**
 * @template {number | string | object} ValueType
 * @typedef {Object} SelectV2Props
 * @prop {ValueType|ValueType[]} [value]
 * @prop {OptionType<ValueType>[]} options
 * @prop {function (ValueType | ValueType[]): void} onChange - callback when the value changes
 * @prop {string} [placeholder] - Show this text when there is no value
 * @prop {boolean} [isMultiple=false] - Allow multiple selections
 * @prop {boolean} [isDisabled=false] - Disable the component
 * @prop {boolean} [isTypeahead=false] - Allow typeahead
 * @prop {ValidatedOptions} [validated] - Show validation state
 * @prop {function (object): string} [iteratee=_.identity] - Return the value to compare
 * @prop {boolean} [isLoading=false] - Show a spinner while loading
 */


/**
 * A select component that supports typeahead and multiple selection
 * @template {number | string | object} ValueType
 * @param {SelectV2Props<ValueType>} props
 * @returns {JSX.Element}
 */
export const SelectV2 = ({
  value,
  options,
  onChange,
  placeholder,
  isMultiple,
  isTypeahead,
  isDisabled,
  validated,
  iteratee = _.identity,
  isLoading = false
}) => {
  const [inputValue, setInputValue] = React.useState(null);
  const [focusedIndex, setFocusedIndex] = React.useState(null);
  const [activeItem, setActiveItem] = React.useState(null);
  const textInputRef = React.useRef(null);
  const [isOpen, showMenu, hideMenu, toggleMenu] = useBoolean();

  const T = useTranslation();
  const onToggleClick = useCallback(() => {
    toggleMenu();
  }, [toggleMenu]);

  const localizedOptions = useMemo(() => {
    return _.map(options, option => {
      return {
        ...option,
        text: T.localize(option.text)
      };
    });
  }, [T, options]);

  const selections = useMemo(() => {
    return _.filter(localizedOptions, option => {
      return _.some(/** @type {array}*/(isMultiple ? value : [value]), val => {
        return iteratee(val) === iteratee(option.value);
      });
    });
  }, [localizedOptions, isMultiple, value, iteratee]);

  const onSelect = useCallback((unusedEvent, selection) => {
    let newValue;
    if (isMultiple) {
      if (_.isArray(value)) {
        if (!_.some(value, val => iteratee(val) === iteratee(selection.value))) {
          newValue = [...(value ?? []), selection.value];
        } else {
          newValue = _.reject(value, val => iteratee(val) === iteratee(selection.value));
        }
      }
    } else {
      newValue = selection.value;
      hideMenu();
    }
    textInputRef.current?.focus();
    setInputValue(null);
    setActiveItem(null);
    setFocusedIndex(null);
    onChange(newValue);
  }, [hideMenu, isMultiple, iteratee, onChange, value]);

  const onClear = useCallback(() => {
    setInputValue(null);
    textInputRef.current?.focus();
    setActiveItem(null);
    setFocusedIndex(null);
    onChange(isMultiple ? [] : null);
  }, [isMultiple, onChange]);

  /**
  * @type {OptionType<ValueType>[]}
  */
  const optionValues = useMemo(() => {
    const defaultCompare = (searchString) => {
      return localizedOptions.filter(option => {
        const localizedLowerString = String(_.toLower(option.text));
        return localizedLowerString.includes(searchString.toLowerCase());
      });

    };
    if (inputValue) {
      showMenu();
      const newOptions = defaultCompare(inputValue);
      if (!newOptions.length) {
        return [{
          text: format(T.common.list.emptyFormatted, inputValue),
          value: T.common.list.empty
        }];
      }
      return newOptions;
    }
    return localizedOptions;
  }, [T.common.list.empty, T.common.list.emptyFormatted, inputValue, localizedOptions, showMenu]);


  const handleMenuArrowKeys = key => {
    let indexToFocus;
    if (isOpen) {
      if (key === KeyTypes.ArrowUp) {
        if (focusedIndex === null || focusedIndex === 0) {
          indexToFocus = optionValues.length - 1;
        } else {
          indexToFocus = focusedIndex - 1;
        }
      }
      if (key === KeyTypes.ArrowDown) {
        if (focusedIndex === null || focusedIndex === optionValues.length - 1) {
          indexToFocus = 0;
        } else {
          indexToFocus = focusedIndex + 1;
        }
      }
      setFocusedIndex(indexToFocus);
      const focusedItem = optionValues[indexToFocus];
      setActiveItem(focusedItem);
    }
  };

  const onInputKeyDown = event => {
    switch (event.key) {
      case KeyTypes.Enter:
        if (!isOpen) {
          toggleMenu();
        } else if (isOpen && _.size(optionValues) > 0 && activeItem) {
          onSelect({}, activeItem);
        }
        break;
      case KeyTypes.Tab:
      case KeyTypes.Escape:
        hideMenu();
        setActiveItem(null);
        break;
      case KeyTypes.ArrowUp:
      case KeyTypes.ArrowDown:
        event.preventDefault();
        handleMenuArrowKeys(event.key);
        break;
      default:
        setFocusedIndex(null);
        break;
    }
  };

  const { menuToggle } = useMenuToggle({
    isMultiple,
    isTypeahead,
    isOpen,
    selected: selections,
    placeholder,
    isDisabled,
    inputValue,
    setInputValue,
    textInputRef,
    onToggleClick,
    onSelect,
    onInputKeyDown: onInputKeyDown,
    onClear: onClear
  });
  // TODO: Fix usage of validated, it's not implemented in the Select component
  _.noop(validated);

  return (
    <Select
      id="multi-typeahead-select"
      isOpen={isOpen}
      selected={selections}
      onSelect={onSelect}
      onOpenChange={() => hideMenu()}
      toggle={menuToggle}
      isScrollable
      popperProps={{ appendTo: document.body }}
    >
      <SelectList isAriaMultiselectable id="select-multi-typeahead-listbox">
        { (isLoading === false) ?
          _.map(optionValues, (option, index) => {
            return (
              <SelectOption
                key={index}
                isFocused={focusedIndex === index}
                id={/** TODO: Check this @type {string}*/(option.value)}
                value={option}
                children={option?.text ?? null}
                description={option?.description ?? null}
              />);
          }) :
          [<SelectOption key={0} value="" children={<Spinner size="md" />} />]
        }
      </SelectList>
    </Select>
  );
};

SelectV2.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.any]),
  options: PropTypes.array,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  isMultiple: PropTypes.bool,
  // isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  validated: PropTypes.oneOf(_.values(ValidatedOptions)),
  iteratee: PropTypes.func,
  isLoading: PropTypes.bool,
  isTypeahead: PropTypes.bool
};
export default React.memo(SelectV2);
